let pending: File[] = [];
let selected: File[] = [];

export type FileSelectionOptions = {
  accept?: string;
  multiple?: boolean;
};

export function setPendingFiles(files: File[]): void {
  pending = files.slice();
  selected = files.slice();
}

export function takePendingFiles(): File[] {
  const out = pending;
  pending = [];
  return out;
}

export function takePendingFilesFor(options: FileSelectionOptions): File[] {
  const currentPending = takePendingFiles();
  if (currentPending.length) {
    return acceptedFiles(currentPending, options);
  }
  return acceptedFiles(selected, options);
}

export function peekPendingFiles(): File[] {
  return pending.slice();
}

export function setSelectedFiles(files: File[]): void {
  selected = files.slice();
}

export function rememberSelectedFiles(files: File[], options: FileSelectionOptions): File[] {
  const accepted = acceptedFiles(files, options);
  if (accepted.length) setSelectedFiles(accepted);
  return accepted;
}

export function peekSelectedFiles(): File[] {
  return selected.slice();
}

export function clearSelectedFiles(): void {
  selected = [];
}

function acceptedFiles(files: File[], options: FileSelectionOptions): File[] {
  const accepted = files.filter((file) => acceptsFile(file, options.accept ?? ''));
  return options.multiple ? accepted : accepted.slice(0, 1);
}

function acceptsFile(file: File, accept: string): boolean {
  const normalized = accept.toLowerCase();
  const kind = detectKind(file);
  if (!normalized) return kind === 'csv';
  if (normalized.includes('application/pdf') || normalized.includes('.pdf')) return kind === 'pdf';
  if (normalized.includes('image/jpeg') || normalized.includes('image/png') || normalized.includes('.jpg') || normalized.includes('.png')) return kind === 'image';
  if (normalized.includes('.docx') || normalized.includes('wordprocessingml')) return kind === 'docx';
  if (normalized.includes('.xlsx') || normalized.includes('spreadsheetml')) return kind === 'xlsx';
  return true;
}

export type FileKind = 'pdf' | 'image' | 'docx' | 'xlsx' | 'csv' | 'unknown';

export function detectKind(file: File): FileKind {
  const name = file.name.toLowerCase();
  const mime = file.type;
  if (mime === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  if (mime.startsWith('image/jpeg') || mime.startsWith('image/png') || /\.(jpe?g|png)$/i.test(name)) return 'image';
  if (name.endsWith('.docx')) return 'docx';
  if (name.endsWith('.xlsx')) return 'xlsx';
  if (name.endsWith('.csv') || name.endsWith('.tsv') || mime === 'text/csv' || mime === 'text/tab-separated-values' || mime === 'text/plain') return 'csv';
  return 'unknown';
}

export type SmartRoute =
  | { kind: 'route'; toolId: string }
  | { kind: 'choose-pdf-action'; file: File }
  | { kind: 'unsupported'; details: string };

export function routeFiles(files: File[]): SmartRoute {
  if (!files.length) return { kind: 'unsupported', details: 'No files selected.' };
  const kinds = files.map(detectKind);
  const unique = new Set(kinds);

  if (unique.size === 1) {
    const k = kinds[0];
    if (k === 'pdf' && files.length === 1) return { kind: 'choose-pdf-action', file: files[0] };
    if (k === 'pdf' && files.length > 1) return { kind: 'route', toolId: 'merge' };
    if (k === 'image') return { kind: 'route', toolId: 'jpg-to-pdf' };
    if (k === 'docx') return { kind: 'route', toolId: 'docx-to-pdf' };
    if (k === 'xlsx') return { kind: 'route', toolId: 'xlsx-to-pdf' };
    if (k === 'csv') return { kind: 'route', toolId: 'csv-to-pdf' };
  }
  return {
    kind: 'unsupported',
    details: `No automatic tool matches this file set: ${[...unique].join(', ')}. Choose a tool manually.`,
  };
}
